import React, { useEffect, useRef, useState } from 'react';
import { TextField, Box } from '@mui/material';
import {
  ProjectStackField,
  ProjectStackDocument,
} from '~/src/entities/project';
import { FieldRenderProps } from './PopulateCardField';
import { useDebounce } from 'react-use';
import {
  getFormattedDateFromIsoStringWithFormat,
  isFullIsoDateString,
} from '~/src/utils/date';

import FieldHeader from './FieldHeader';
import { isEqual } from 'lodash';

export type ShortTextFieldProps = {
  field: FieldRenderProps;
  projectField: ProjectStackField;
  projectStackDocuments: ProjectStackDocument[];
  onFirstChange?: () => void;
  hasActiveMapping?: boolean;
  dateFormat?: string | null;
};

export const ShortTextField = React.memo(
  ({
    field,
    projectField,
    projectStackDocuments,
    onFirstChange,
    hasActiveMapping,
    dateFormat,
  }: ShortTextFieldProps) => {
    const { onChange, countTouched, ref, value, ...rest } = field;
    const [userValue, setUserValue] = useState('');
    const isFormattedValueRef = useRef(false);
    useDebounce(
      () => {
        if (isFormattedValueRef.current) {
          isFormattedValueRef.current = false;
          return;
        }
        if (userValue !== undefined && userValue !== value) onChange(userValue);
      },
      1000,
      [userValue],
    );

    useEffect(() => {
      if (value == undefined) return;

      if (isFullIsoDateString(value)) {
        const formatted = getFormattedDateFromIsoStringWithFormat(
          value,
          dateFormat,
        );
        if (formatted) {
          isFormattedValueRef.current = true;
          setUserValue(formatted);
          onChange(formatted);
          return;
        }
      }
      setUserValue(value);
    }, [value, dateFormat, onChange]);

    return (
      <Box>
        <Box>
          <FieldHeader
            projectField={projectField}
            projectStackDocuments={projectStackDocuments}
          />
        </Box>
        <Box>
          <TextField
            size={'small'}
            fullWidth
            {...rest}
            value={userValue}
            id={field.name}
            inputRef={ref}
            inputProps={{
              'data-1p-ignore': true,
            }}
            sx={
              hasActiveMapping
                ? {
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: (theme) => theme.palette.blue[50],
                    },
                  }
                : undefined
            }
            onChange={(e) => {
              isFormattedValueRef.current = false;
              onFirstChange?.();
              setUserValue(e.target.value);
              if (countTouched) {
                countTouched(field.name);
              }
            }}
          />
        </Box>
      </Box>
    );
  },
  (prevProps, nextProps) =>
    isEqual(prevProps.field.value, nextProps.field.value) &&
    isEqual(prevProps.projectField, nextProps.projectField) &&
    isEqual(prevProps.projectStackDocuments, nextProps.projectStackDocuments) &&
    prevProps.onFirstChange === nextProps.onFirstChange &&
    prevProps.hasActiveMapping === nextProps.hasActiveMapping &&
    prevProps.dateFormat === nextProps.dateFormat,
);
